import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../lib/api';
import { toast } from 'sonner';
import { Lock, ArrowRight, CheckCircle, Warning } from '@phosphor-icons/react';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing reset token in the URL.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      toast.success('Password reset successfully');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to reset password. The link may be expired or already used.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-card border border-border rounded-sm shadow-xl p-8">
        <div className="mb-6">
          <div
            className="h-12 w-14 flex items-center justify-center text-white font-heading font-bold text-base mb-4"
            style={{ background: '#002677' }}
          >
            BIL
          </div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Reset password</h1>
          <p className="text-sm text-muted-foreground mt-1">Choose a new password to regain access to your account.</p>
        </div>

        {!token && (
          <div className="p-3 mb-4 text-sm bg-destructive/10 text-destructive border border-destructive/20 rounded-sm flex items-start gap-2">
            <Warning size={18} className="flex-shrink-0 mt-0.5" />
            <span>Missing reset token. Please use the one-time link you received.</span>
          </div>
        )}

        {error && (
          <div className="p-3 mb-4 text-sm bg-destructive/10 text-destructive border border-destructive/20 rounded-sm" data-testid="reset-error">
            {error}
          </div>
        )}

        {done ? (
          <div className="p-4 bg-success/10 border border-success/20 rounded-sm flex items-start gap-3" data-testid="reset-success">
            <CheckCircle size={24} weight="fill" className="text-success flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Password updated</p>
              <p className="text-muted-foreground mt-1">Redirecting to sign-in…</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 rounded-sm"
                  placeholder="At least 6 characters"
                  required
                  data-testid="reset-password"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-10 rounded-sm"
                  placeholder="Re-enter password"
                  required
                  data-testid="reset-confirm"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full rounded-sm font-medium h-11"
              style={{ background: '#002677' }}
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? 'Updating…' : 'Update password'}
              <ArrowRight className="ml-2" size={18} />
            </Button>
            <p className="text-center text-sm text-muted-foreground pt-1">
              <Link to="/login" className="text-primary hover:underline font-medium">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
